import React from 'react';
import Typography from '@mui/material/Typography';
import OutlinedInput from '@mui/material/OutlinedInput';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Box from '@mui/material/Box';
import useWithdrawalCoinOptions from '../../hooks/useWithdrawalCoinOptions';

const COINS_WITH_DESTINATION_TAG = ['XRP'];

const labelSx = {
  display: 'block',
  mb: 0.75,
  fontSize: 11,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  color: 'text.secondary',
  fontWeight: 600,
};

const fieldSx = {
  width: '100%',
  bgcolor: 'action.hover',
  borderRadius: 2,
  color: 'text.primary',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
  '& .MuiSelect-icon': { color: 'text.secondary' },
};

export default function WithdrawalFormFields({
  amount,
  setAmount,
  coinName,
  setCoinName,
  address,
  setAddress,
  destinationTag = '',
  setDestinationTag,
  error = {},
}) {
  const {
    coinOptions,
    estimateCoinDeduction,
    formatUsd,
    formatCryptoAmount,
    getCoinUnits,
    getCoinUsd,
    coinRates,
  } = useWithdrawalCoinOptions();

  const usdAmount = parseFloat(amount) || 0;
  const { coinAmount: coinToDeduct, priceUsd } = estimateCoinDeduction(coinName, usdAmount);
  const availableUnits = coinName ? getCoinUnits(coinName) : 0;
  const availableUsd = coinName ? getCoinUsd(coinName) : 0;

  return (
    <Box sx={{ fontFamily: '"Source Sans 3", "Segoe UI", sans-serif' }}>
      <Box sx={{ mb: 2.5 }}>
        <Typography component="label" htmlFor="amount" sx={labelSx}>Amount</Typography>
        <OutlinedInput
          onChange={(e) => setAmount(e.target.value)}
          id="amount"
          type="number"
          name="amount"
          placeholder="0.00"
          value={amount}
          error={Boolean(error?.amount)}
          startAdornment={<Typography sx={{ mr: 1, color: 'text.secondary', fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: 28 }}>$</Typography>}
          sx={{
            ...fieldSx,
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            fontSize: 32,
            fontWeight: 600,
            '& input': { py: 1.2 },
          }}
        />
        {error?.amount && (
          <Typography color="error" variant="caption">{error.amount}</Typography>
        )}
      </Box>

      {coinRates.length > 0 && (
        <Box sx={{ mb: 2.5 }}>
          <Typography component="label" id="withdraw-coin-label" sx={labelSx}>Asset</Typography>
          <Select
            labelId="withdraw-coin-label"
            onChange={(e) => setCoinName(e.target.value)}
            id="coinName"
            name="coinName"
            displayEmpty
            value={coinName}
            error={Boolean(error?.coin_name)}
            sx={fieldSx}
          >
            <MenuItem value="">Choose an asset</MenuItem>
            {coinOptions.map((coin) => (
              <MenuItem key={coin.symbol} value={coin.symbol} disabled={coin.units <= 0}>
                {coin.symbol}
                {coin.units > 0
                  ? ` · ${formatCryptoAmount(coin.units)} · ${formatUsd(coin.usdBalance)}`
                  : ' · no balance'}
              </MenuItem>
            ))}
          </Select>
          {coinName && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75, letterSpacing: '0.04em' }}>
              Available {formatCryptoAmount(availableUnits)} {coinName} · {formatUsd(availableUsd)}
            </Typography>
          )}
        </Box>
      )}

      {coinName && usdAmount > 0 && (
        <Box sx={{ mb: 2.5, px: 0.5 }}>
          {priceUsd > 0 ? (
            <>
              <Typography sx={{ ...labelSx, mb: 0.25 }}>You send</Typography>
              <Typography sx={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 600, fontSize: 26, color: 'text.primary' }}>
                {formatCryptoAmount(coinToDeduct)} {coinName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatUsd(usdAmount)} at {formatUsd(priceUsd)} per {coinName}
              </Typography>
            </>
          ) : (
            <Typography variant="caption" color="warning.main">
              Price unavailable for {coinName}.
            </Typography>
          )}
        </Box>
      )}

      <Box sx={{ mb: 2.5 }}>
        <Typography component="label" htmlFor="address" sx={labelSx}>Destination</Typography>
        <OutlinedInput
          onChange={(e) => setAddress(e.target.value)}
          id="address"
          type="text"
          name="address"
          placeholder="Wallet address"
          value={address}
          error={Boolean(error?.address)}
          sx={{ ...fieldSx, fontSize: 15, '& input': { py: 1.4 } }}
        />
        {error?.address && (
          <Typography color="error" variant="caption">{error.address}</Typography>
        )}
      </Box>

      {COINS_WITH_DESTINATION_TAG.includes(coinName) && setDestinationTag && (
        <Box sx={{ mb: 1 }}>
          <Typography component="label" htmlFor="destinationTag" sx={labelSx}>Destination tag</Typography>
          <OutlinedInput
            onChange={(e) => setDestinationTag(e.target.value.replace(/\D/g, ''))}
            id="destinationTag"
            type="text"
            inputMode="numeric"
            name="destinationTag"
            placeholder="Optional"
            value={destinationTag}
            error={Boolean(error?.destination_tag)}
            sx={{ ...fieldSx, '& input': { py: 1.4 } }}
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
            Most XRP exchanges require a tag. Leave this blank only if the receiving wallet does not use one.
          </Typography>
          {error?.destination_tag && (
            <Typography color="error" variant="caption">{error.destination_tag}</Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

export { useWithdrawalCoinOptions };
